import { Rectangle, Texture } from 'pixi.js';
import { PIECE_VALUES } from '@blind-kings/game-core';
import {
  pickStrip,
  resolveSprite,
  stripFrameRect,
  type SheetSpriteDef,
  type SheetStrip,
  type SpriteAnimState,
} from '../../../characters';
import type { TeamPalette } from '../../../player-colors';
import { UNTURNED_COLOR } from '../../../player-colors';
import { mapWithConcurrency } from '../../../utils/concurrency';
import {
  corePreloadPlan,
  matchPreloadPlan,
  paletteForTeam,
  preloadTaskKey,
  spritePackForTeam,
  type PieceTint,
  type PreloadTask,
} from './preload-plan';
import unturnedSvg from '../../../characters/icons/unturned.svg?raw';
import peasantSvg from '../../../characters/icons/peasant.svg?raw';
import assassinSvg from '../../../characters/icons/assassin.svg?raw';
import knightSvg from '../../../characters/icons/knight.svg?raw';
import cavalrySvg from '../../../characters/icons/cavalry.svg?raw';
import mageSvg from '../../../characters/icons/mage.svg?raw';
import heroSvg from '../../../characters/icons/hero.svg?raw';
import kingSvg from '../../../characters/icons/king.svg?raw';

export type { PieceTint } from './preload-plan';
export { paletteForTeam, spritePackForTeam } from './preload-plan';

/** Parallel downloads during preload — enough to fill the pipe, not stall it. */
const PRELOAD_CONCURRENCY = 6;

const PIECE_SVGS: Record<number, string> = {
  [PIECE_VALUES.Unturned]: unturnedSvg,
  [PIECE_VALUES.Assassin]: assassinSvg,
  [PIECE_VALUES.Peasant]: peasantSvg,
  [PIECE_VALUES.Knight]: knightSvg,
  [PIECE_VALUES.Cavalry]: cavalrySvg,
  [PIECE_VALUES.Mage]: mageSvg,
  [PIECE_VALUES.Hero]: heroSvg,
  [PIECE_VALUES.King]: kingSvg,
};

const svgTexturePromises = new Map<string, Promise<Texture>>();
const stripTexturePromises = new Map<string, Promise<Texture>>();
const stripFramePromises = new Map<string, Promise<Texture[]>>();
const visualPromises = new Map<string, Promise<PieceVisual>>();
const matchPreloadPromises = new Map<string, Promise<void>>();
let boardTexturePromise: Promise<Texture> | null = null;
let corePreloadPromise: Promise<void> | null = null;

function loadTextureFromUrl(url: string): Promise<Texture> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(Texture.from(img));
    img.onerror = () => reject(new Error(`Failed to load ${url}`));
    img.src = url;
  });
}

export async function loadBoardTexture(): Promise<Texture> {
  if (!boardTexturePromise) {
    boardTexturePromise = loadTextureFromUrl('/board.jpg');
  }
  return boardTexturePromise;
}

function sizedSvg(svgText: string, size = 256): string {
  if (/\swidth=/i.test(svgText) && /\sheight=/i.test(svgText)) return svgText;
  return svgText.replace(/<svg\b/i, `<svg width="${size}" height="${size}"`);
}

function loadSvgTexture(svgText: string): Promise<Texture> {
  const blobUrl = URL.createObjectURL(
    new Blob([sizedSvg(svgText)], { type: 'image/svg+xml;charset=utf-8' })
  );
  return loadTextureFromUrl(blobUrl);
}

function colorizeSvg(svgText: string, fill: string, stroke: string): string {
  const style = `<style type="text/css">
.st0{fill:${fill};stroke:${stroke};stroke-width:1;stroke-linejoin:round;}
.st1{fill:none;stroke:${stroke};stroke-width:1;stroke-linejoin:round;}
*{fill:${fill};stroke:${stroke};}
</style>`;

  if (/<style[\s\S]*?<\/style>/i.test(svgText)) {
    return svgText.replace(/<style[\s\S]*?<\/style>/i, style);
  }
  return svgText.replace(/<svg([^>]*)>/i, `<svg$1>${style}`);
}

function loadSvgPieceTexture(
  pieceValue: number,
  tint: PieceTint
): Promise<Texture> {
  const raw = PIECE_SVGS[pieceValue];
  if (!raw) return Promise.resolve(Texture.EMPTY);

  const key = `svg:${pieceValue}:${tint.fill}:${tint.stroke}`;
  const cached = svgTexturePromises.get(key);
  if (cached) return cached;

  const colors =
    pieceValue === PIECE_VALUES.Unturned
      ? { fill: UNTURNED_COLOR.fill, stroke: UNTURNED_COLOR.stroke }
      : tint;

  const promise = loadSvgTexture(
    colorizeSvg(raw, colors.fill, colors.stroke)
  );

  svgTexturePromises.set(key, promise);
  return promise;
}

function loadImageTexture(url: string): Promise<Texture> {
  const cached = stripTexturePromises.get(url);
  if (cached) return cached;
  const promise = loadTextureFromUrl(url);
  stripTexturePromises.set(url, promise);
  return promise;
}

/** Slice one horizontal strip into per-frame textures sharing a single source. */
function loadStripFrames(
  def: SheetSpriteDef,
  strip: SheetStrip
): Promise<Texture[]> {
  const cached = stripFramePromises.get(strip.url);
  if (cached) return cached;

  const promise = (async () => {
    const sheet = await loadImageTexture(strip.url);
    // Guard against a strip whose art has fewer columns than the manifest claims.
    const available = Math.floor(sheet.width / def.frameWidth) || 1;
    const frameCount = Math.max(1, Math.min(strip.frameCount, available));
    return Array.from({ length: frameCount }, (_, i) => {
      const rect = stripFrameRect(def, strip, i);
      return new Texture({
        source: sheet.source,
        frame: new Rectangle(rect.x, rect.y, rect.width, rect.height),
      });
    });
  })();

  stripFramePromises.set(strip.url, promise);
  return promise;
}

export type PieceVisual = {
  frames: Texture[];
  fps: number;
  visualKey: string;
};

/** Resolve SVG or strip visuals for a piece from its team's sprite pack. */
export function loadPieceVisual(
  pieceValue: number,
  tint: PieceTint,
  spritePackId = 'classic',
  animState: SpriteAnimState = 'resting'
): Promise<PieceVisual> {
  const key = preloadTaskKey({
    pieceValue,
    packId: spritePackId,
    state: animState,
    tint,
  });
  const cached = visualPromises.get(key);
  if (cached) return cached;

  const def = resolveSprite(pieceValue, spritePackId);

  const promise = (async (): Promise<PieceVisual> => {
    if (def?.kind === 'sheet') {
      const strip = pickStrip(def, animState);
      const frames = await loadStripFrames(def, strip);
      return { frames, fps: strip.fps, visualKey: key };
    }

    const texture = await loadSvgPieceTexture(pieceValue, tint);
    return { frames: [texture], fps: 0, visualKey: key };
  })();

  visualPromises.set(key, promise);
  return promise;
}

async function runPreload(tasks: readonly PreloadTask[]): Promise<void> {
  await mapWithConcurrency(tasks, PRELOAD_CONCURRENCY, async (task) => {
    // A missing strip must never block boot or the reveal countdown.
    await loadPieceVisual(
      task.pieceValue,
      task.tint,
      task.packId,
      task.state
    ).catch(() => undefined);
  });
}

/** Warm the still art the reserve rails and store render outside of Pixi. */
async function preloadStaticPortraits(packIds: readonly string[]) {
  const urls = new Set<string>();
  for (const packId of packIds) {
    for (const pieceValue of Object.keys(PIECE_SVGS).map(Number)) {
      const def = resolveSprite(pieceValue, packId);
      if (def?.kind === 'sheet') urls.add(def.staticUrl);
    }
  }
  await mapWithConcurrency([...urls], PRELOAD_CONCURRENCY, async (url) => {
    await loadImageTexture(url).catch(() => undefined);
  });
}

/** Small blocking set needed before the board can draw anything. Idempotent. */
export function preloadCoreAssets(): Promise<void> {
  if (!corePreloadPromise) {
    corePreloadPromise = Promise.all([
      loadBoardTexture(),
      runPreload(corePreloadPlan()),
    ]).then(() => undefined);
  }
  return corePreloadPromise;
}

/**
 * Warm every strip the two seats can play. Safe to call repeatedly — each
 * distinct matchup resolves once and later calls share that work.
 */
export function preloadMatchAssets(
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>
): Promise<void> {
  const packIds = [
    spritePackForTeam(0, teamPalettes),
    spritePackForTeam(1, teamPalettes),
  ];
  // Tints are part of the key: classic pieces are recolored per army skin.
  const key = ([0, 1] as const)
    .map((seat) => {
      const tint = paletteForTeam(seat, teamPalettes);
      return `${packIds[seat]}:${tint.fill}:${tint.stroke}`;
    })
    .join('|');
  const inFlight = matchPreloadPromises.get(key);
  if (inFlight) return inFlight;

  const promise = Promise.all([
    runPreload(matchPreloadPlan(teamPalettes)),
    preloadStaticPortraits(packIds),
  ]).then(() => undefined);

  matchPreloadPromises.set(key, promise);
  return promise;
}
